/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Typography,
  Slider,
  Tooltip,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  CheckCircle as ActiveIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const InvoiceTemplateManagement = () => {
  const [templates, setTemplates] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Upload dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [headerHeight, setHeaderHeight] = useState(160);
  const [footerMargin, setFooterMargin] = useState(80);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  
  // Preview dialog
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  
  const fileInputRef = useRef(null);
  const previewTimeoutRef = useRef(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const [templatesRes, activeRes] = await Promise.all([
        axios.get('/api/invoice-templates'),
        axios.get('/api/invoice-templates/active')
      ]);
      
      setTemplates(templatesRes.data || []);
      if (activeRes.data.hasTemplate) {
        setActiveTemplate(activeRes.data.template);
      } else {
        setActiveTemplate(null);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to fetch templates');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Please select a PDF file');
        return;
      }
      setSelectedFile(file);
      setUploadName(file.name.replace('.pdf', ''));
      setPreviewUrl(null);
    }
  };

  const generatePreview = useCallback(async (file, hHeight, fMargin) => {
    if (!file) return;
    
    setPreviewLoading(true);
    try {
      const formData = new FormData();
      formData.append('template', file);
      formData.append('header_height', hHeight);
      formData.append('footer_margin', fMargin);

      const response = await axios.post('/api/invoice-templates/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob'
      });

      // Revoke old URL if exists
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      const url = URL.createObjectURL(response.data);
      setPreviewUrl(url);
    } catch (err) {
      console.error('Error generating preview:', err);
      setError('Failed to generate preview. Please ensure the file is a valid PDF.');
    } finally {
      setPreviewLoading(false);
    }
  }, [previewUrl]);

  const handleGeneratePreview = async () => {
    if (!selectedFile) return;
    await generatePreview(selectedFile, headerHeight, footerMargin);
    setPreviewDialogOpen(true);
  };

  // Debounced preview update when sliders change
  const updatePreviewDebounced = useCallback((hHeight, fMargin) => {
    if (!selectedFile || !previewDialogOpen) return;
    
    // Clear any pending timeout
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }
    
    // Set new timeout for debounced update
    previewTimeoutRef.current = setTimeout(() => {
      generatePreview(selectedFile, hHeight, fMargin);
    }, 300);
  }, [selectedFile, previewDialogOpen, generatePreview]);

  const handleHeaderHeightChange = (e, value) => {
    setHeaderHeight(value);
    updatePreviewDebounced(value, footerMargin);
  };

  const handleFooterMarginChange = (e, value) => {
    setFooterMargin(value);
    updatePreviewDebounced(headerHeight, value);
  };

  const handleUpload = async (activate = false) => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('template', selectedFile);
      formData.append('name', uploadName || 'Custom Template');
      formData.append('header_height', headerHeight);
      formData.append('footer_margin', footerMargin);
      formData.append('activate', activate);

      await axios.post('/api/invoice-templates', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess(`Template uploaded successfully${activate ? ' and activated' : ''}`);
      setUploadDialogOpen(false);
      setPreviewDialogOpen(false);
      resetUploadForm();
      fetchTemplates();
    } catch (err) {
      console.error('Error uploading template:', err);
      setError(err.response?.data?.error || 'Failed to upload template');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setSelectedFile(null);
    setUploadName('');
    setHeaderHeight(160);
    setFooterMargin(80);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleActivate = async (templateId) => {
    try {
      await axios.put(`/api/invoice-templates/${templateId}/activate`);
      setSuccess('Template activated successfully');
      fetchTemplates();
    } catch (err) {
      console.error('Error activating template:', err);
      setError(err.response?.data?.error || 'Failed to activate template');
    }
  };

  const handleDownload = async (templateId, filename) => {
    try {
      const response = await axios.get(`/api/invoice-templates/${templateId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'template.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading template:', err);
      setError('Failed to download template');
    }
  };

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    try {
      await axios.delete(`/api/invoice-templates/${templateToDelete._id}`);
      setSuccess('Template deleted successfully');
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(err.response?.data?.error || 'Failed to delete template');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="management-container">
      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Current Active Template Card */}
      <Card sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <ActiveIcon color="success" />
            <Typography variant="h6">Active Invoice Template</Typography>
          </Box>
          
          {activeTemplate ? (
            <Box>
              <Typography variant="body1"><strong>Name:</strong> {activeTemplate.name}</Typography>
              <Typography variant="body2" color="textSecondary">
                File: {activeTemplate.original_filename}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Uploaded: {formatDate(activeTemplate.uploaded_at)}
                {activeTemplate.uploaded_by && ` by ${activeTemplate.uploaded_by.name || activeTemplate.uploaded_by.email}`}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Header Height: {activeTemplate.header_height}px | Footer Margin: {activeTemplate.footer_margin}px
              </Typography>
              <Box mt={2}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload(activeTemplate._id, activeTemplate.original_filename)}
                  sx={{ mr: 1 }}
                >
                  Download
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="body1" color="textSecondary">
                No custom template active. Using default system template.
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                <InfoIcon sx={{ mr: 1 }} />
                Upload a custom template to personalize your invoices.
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Upload Button */}
      <Box display="flex" justifyContent="flex-start" mb={3}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<UploadIcon />}
          onClick={() => setUploadDialogOpen(true)}
        >
          Upload New Template
        </Button>
      </Box>

      {/* Templates Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Name</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>File</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Uploaded</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Settings</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    No templates uploaded yet. Upload a template to customize your invoices.
                  </td>
                </tr>
              ) : (
                templates.map((template) => (
                  <tr key={template._id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{template.name}</td>
                    <td style={{ padding: '12px' }}>{template.original_filename}</td>
                    <td style={{ padding: '12px' }}>
                      {formatDate(template.uploaded_at)}
                      {template.uploaded_by && (
                        <Typography variant="caption" display="block" color="textSecondary">
                          by {template.uploaded_by.name || template.uploaded_by.email}
                        </Typography>
                      )}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <Typography variant="caption">
                        H: {template.header_height}px | F: {template.footer_margin}px
                      </Typography>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {template.is_active ? (
                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                          <ActiveIcon color="success" fontSize="small" />
                          <Typography variant="body2" color="success.main">Active</Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">Inactive</Typography>
                      )}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(template._id, template.original_filename)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!template.is_active && (
                        <>
                          <Tooltip title="Activate">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleActivate(template._id)}
                            >
                              <ActiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(template)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Box>
      </Paper>

      {/* Template Requirements Info */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>Template Requirements:</Typography>
        <Typography variant="body2">
          - Template must be a PDF file (max 10MB)
          <br />
          - The header area (logo, company info) should be at the top of the page
          <br />
          - Invoice content will be placed below the header height you specify
          <br />
          - Footer area will be reserved at the bottom for page numbers
          <br />
          - Use the preview feature to verify your settings before activating
        </Typography>
      </Alert>

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={() => {
          setUploadDialogOpen(false);
          resetUploadForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload New Invoice Template</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* File Selection */}
            <Box mb={3}>
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileSelect}
                ref={fileInputRef}
                style={{ display: 'none' }}
                id="template-upload"
              />
              <label htmlFor="template-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                >
                  Select PDF File
                </Button>
              </label>
              {selectedFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </Typography>
              )}
            </Box>

            {/* Template Name */}
            <TextField
              fullWidth
              label="Template Name"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              margin="normal"
              helperText="A friendly name to identify this template"
            />

            {/* Preview Button */}
            {selectedFile && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={previewLoading ? <CircularProgress size={20} /> : <PreviewIcon />}
                  onClick={handleGeneratePreview}
                  disabled={previewLoading}
                >
                  {previewLoading ? 'Generating Preview...' : 'Preview & Adjust Settings'}
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUploadDialogOpen(false);
            resetUploadForm();
          }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog with Sliders */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => {
          setPreviewDialogOpen(false);
        }}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle>
          Preview Invoice Template
          {previewLoading && <CircularProgress size={20} sx={{ ml: 2 }} />}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" gap={3} sx={{ height: 'calc(100% - 20px)' }}>
            {/* Settings Panel on the Left */}
            <Box sx={{ width: 280, flexShrink: 0, pt: 1 }}>
              <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                <Typography variant="h6" gutterBottom>Layout Settings</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Adjust the sliders to set where invoice content begins and ends on the page.
                </Typography>

                {/* Header Height Slider */}
                <Box mb={4}>
                  <Typography gutterBottom>
                    <strong>Header Height: {headerHeight}px</strong>
                    <Tooltip title="Space reserved for your letterhead/logo at the top. Content starts below this line.">
                      <InfoIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle', color: 'text.secondary' }} />
                    </Tooltip>
                  </Typography>
                  <Slider
                    value={headerHeight}
                    onChange={handleHeaderHeightChange}
                    min={50}
                    max={300}
                    step={1}
                    valueLabelDisplay="auto"
                    marks={[
                      { value: 50, label: '50' },
                      { value: 160, label: '160' },
                      { value: 300, label: '300' }
                    ]}
                  />
                </Box>

                {/* Footer Margin Slider */}
                <Box mb={3}>
                  <Typography gutterBottom>
                    <strong>Footer Margin: {footerMargin}px</strong>
                    <Tooltip title="Space reserved at the bottom for page numbers. Content stops above this line.">
                      <InfoIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle', color: 'text.secondary' }} />
                    </Tooltip>
                  </Typography>
                  <Slider
                    value={footerMargin}
                    onChange={handleFooterMarginChange}
                    min={40}
                    max={150}
                    step={1}
                    valueLabelDisplay="auto"
                    marks={[
                      { value: 40, label: '40' },
                      { value: 80, label: '80' },
                      { value: 150, label: '150' }
                    ]}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  <strong>Blue line:</strong> Header boundary (content starts below)
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  <strong>Orange line:</strong> Footer boundary (content stops above)
                </Typography>

                <Divider sx={{ my: 2 }} />

                {/* Upload Actions */}
                <Box display="flex" flexDirection="column" gap={1}>
                  <Button
                    variant="outlined"
                    onClick={() => handleUpload(false)}
                    disabled={!selectedFile || uploading}
                    fullWidth
                  >
                    Save (Inactive)
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleUpload(true)}
                    disabled={!selectedFile || uploading}
                    startIcon={uploading ? <CircularProgress size={20} /> : null}
                    fullWidth
                  >
                    {uploading ? 'Saving...' : 'Save & Activate'}
                  </Button>
                </Box>
              </Paper>
            </Box>

            {/* Preview Panel on the Right */}
            <Box sx={{ flexGrow: 1, height: '100%', position: 'relative' }}>
              {previewUrl ? (
                <iframe
                  src={previewUrl}
                  width="100%"
                  height="100%"
                  title="Invoice Preview"
                  style={{ border: '1px solid #ccc', borderRadius: 4 }}
                />
              ) : (
                <Box 
                  display="flex" 
                  justifyContent="center" 
                  alignItems="center" 
                  height="100%"
                  sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}
                >
                  <CircularProgress />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPreviewDialogOpen(false);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the template "{templateToDelete?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default InvoiceTemplateManagement;
